import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import KPICard from "@/components/KPICard";
import { Card } from "@/components/ui/card";
import { api } from "@/lib/api";
import {
  TrendingUp,
  Building2,
  Activity,
  Users,
  AlertTriangle,
  DollarSign,
  Repeat,
  PieChart as PieIcon,
} from "lucide-react";
import { TID } from "@/constants/testIds";
import { formatBRL } from "@/lib/format";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";

const COLORS = ["#065f46", "#f59e0b", "#dc2626", "#a8a29e"];

export default function AdminMetrics() {
  const [m, setM] = useState(null);
  const [chartsReady, setChartsReady] = useState(false);

  useEffect(() => {
    api
      .get("/admin/metrics")
      .then(({ data }) => setM(data))
      .catch((err) => console.error("Failed to load metrics", err));
  }, []);

  // Defer chart mount until after data + layout settle.
  // Recharts ResponsiveContainer reports -1 dimensions on the very first
  // ResizeObserver tick before the parent flexbox measures, so we wait until
  // the next paint cycle to mount it.
  useEffect(() => {
    if (!m) return;
    const t = setTimeout(() => setChartsReady(true), 60);
    return () => clearTimeout(t);
  }, [m]);

  const pie = m
    ? [
        { name: "Ativas", value: m.active_companies },
        { name: "Trial", value: m.trial_companies },
        { name: "Canceladas", value: m.cancelled_companies },
      ]
    : [];

  const bar = m
    ? [
        { name: "MRR", value: m.mrr },
        { name: "Ticket Médio", value: m.avg_ticket },
        { name: "LTV", value: m.ltv },
      ]
    : [];

  return (
    <Layout title="Métricas SaaS" subtitle="Saúde financeira e operacional da plataforma">
      <div data-testid={TID.adminMetrics} className="space-y-6">
        <section className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard label="MRR" value={formatBRL(m?.mrr || 0)} hint="Receita mensal recorrente" icon={DollarSign} tone="success" testId="metric-mrr" />
          <KPICard label="ARR" value={formatBRL(m?.arr || 0)} hint="Receita anual recorrente" icon={TrendingUp} tone="success" testId="metric-arr" />
          <KPICard label="Receita total" value={formatBRL(m?.revenue_total || 0)} hint="Soma de pagamentos confirmados" icon={Activity} testId="metric-revenue" />
          <KPICard label="Ticket médio" value={formatBRL(m?.avg_ticket || 0)} hint="Por transação paga" icon={Repeat} testId="metric-avg" />
        </section>

        <section className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard label="Empresas totais" value={m?.total_companies || 0} icon={Building2} testId="metric-companies" />
          <KPICard label="Empresas ativas" value={m?.active_companies || 0} icon={Users} tone="success" testId="metric-active" />
          <KPICard label="Churn" value={`${(m?.churn_rate || 0).toFixed(1)}%`} icon={AlertTriangle} tone={(m?.churn_rate || 0) > 5 ? "danger" : "default"} testId="metric-churn" />
          <KPICard label="Tickets abertos" value={m?.open_tickets || 0} icon={AlertTriangle} tone={(m?.open_tickets || 0) > 0 ? "warn" : "default"} testId="metric-tickets" />
        </section>

        <section className="grid lg:grid-cols-2 gap-5">
          <Card className="p-6 rounded-xl border-stone-200 bg-white">
            <div className="flex items-center gap-2 mb-4">
              <PieIcon className="h-4 w-4 text-emerald-800" />
              <h3 className="font-display text-lg font-bold tracking-tight">Distribuição de empresas</h3>
            </div>
            <div style={{ width: "100%", height: 256 }}>
              {chartsReady && (
                <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                  <PieChart>
                    <Pie data={pie} dataKey="value" nameKey="name" outerRadius={80} label>
                      {pie.map((slice) => (
                        <Cell key={slice.name} fill={COLORS[pie.indexOf(slice) % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </Card>
          <Card className="p-6 rounded-xl border-stone-200 bg-white">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="h-4 w-4 text-emerald-800" />
              <h3 className="font-display text-lg font-bold tracking-tight">Indicadores financeiros</h3>
            </div>
            <div style={{ width: "100%", height: 256 }}>
              {chartsReady && (
                <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                  <BarChart data={bar}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                    <XAxis dataKey="name" stroke="#78716c" tick={{ fontSize: 12 }} />
                    <YAxis stroke="#78716c" tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Bar dataKey="value" fill="#065f46" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </Card>
        </section>
      </div>
    </Layout>
  );
}
